import React, { useCallback } from 'react';
import ReactDom from 'react-dom';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';

import {
  AccountManager,
  NotificationManager,
  QuickBetslipProvider,
  SiteConfigManager,
  useApplicationState,
  useBetslipState,
  useBetslipStateUpdate,
} from '@atlas/core';
import { staticRoutes } from '@atlas/routing';
import { Button, currencySymbols, Icon, Label, MoneyInput } from '@atlas/ui';

import NoMoneyButton from 'common/Betslip/BetsMoneyBlock/NoMoneyButton';
import { useLocalBetslip } from 'common/Betslip/LocalBetslipContext';
import { useAccountBlocked } from 'hooks/useAccountBlocked';
import { useSelfRegulationContext } from 'src/components/SelfRegulationProvider';

import QuickBetInfo from './QuickBetInfo';

const QuickBetAgreeButton = ({ desiredAmount }) => {
  const applicationState = useApplicationState();
  const {
    authenticated,
    resolved,
    wallet: {
      currency,
      detailedBalance: { BONUS: balanceBonus, CASH: balanceCash, FROZEN: balanceFrozen } = {},
    } = {},
  } = applicationState;

  const accountBlocked = useAccountBlocked();

  const overallBalance = balanceBonus + balanceCash + balanceFrozen;

  const { amount } = useBetslipState();
  const { setIsOpenPopup } = useLocalBetslip();
  const { showSelfRegulationPopupIfNeeded, showSpentLimitPopupIfNeeded } =
    useSelfRegulationContext();

  const betslipStateUpdate = useBetslipStateUpdate();

  const onConfirmQuickBet = React.useCallback(async () => {
    if (accountBlocked) {
      setIsOpenPopup(false);
      NotificationManager.display({
        id: 'accountBlocked',
        type: 'accountBlocked',
      });
      return;
    }

    const canProceedSpentLimit = await showSpentLimitPopupIfNeeded();
    if (!canProceedSpentLimit) return;

    const canProceedSelfRegulation = await showSelfRegulationPopupIfNeeded();
    if (!canProceedSelfRegulation) return;

    betslipStateUpdate({ amount: desiredAmount });
    setIsOpenPopup(false);
  }, [
    accountBlocked,
    showSpentLimitPopupIfNeeded,
    showSelfRegulationPopupIfNeeded,
    betslipStateUpdate,
    desiredAmount,
    setIsOpenPopup,
  ]);

  if (resolved && !authenticated) {
    return (
      <div className="sticky-bottom-buttons-container">
        <Link to={staticRoutes.signin}>
          <Button label="quick_bet_auth_required" size="big" type="primary" />
        </Link>
      </div>
    );
  }

  if (overallBalance < desiredAmount) {
    return (
      <div className="sticky-bottom-buttons-container">
        <NoMoneyButton className="btn primary big" />
      </div>
    );
  }

  if (amount && desiredAmount === amount) {
    const currencySymbol = currencySymbols[currency] || '';

    return (
      <div className="betslip-notification">
        <Icon className="bet-report-header__status" name="success" />
        <div className="bet-report-header__container">
          <Label
            className="bet-report-header__label title-small-text"
            message="quick_bet_mode_on"
            params={{ amount, currency, currencySymbol }}
          />
          <Label
            className="bet-report-header__label sub-title-xxsmall-300-text"
            message="quick_bet_click_on_outcome"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="sticky-bottom-buttons-container">
      <Button
        disabled={!desiredAmount}
        label={!amount ? 'quick_bet_start_to_place' : 'quick_bet_change_bet_amount'}
        onClick={onConfirmQuickBet}
        size="big"
        type="primary"
      />
    </div>
  );
};

export default function () {
  const isSimplifiedView = SiteConfigManager.getConfig(
    'config.config.sportsbook.mobileGridViewType',
  );
  return (
    <QuickBetslipProvider>
      <QuickBet />
      {ReactDom.createPortal(
        <QuickBetInfo />,
        isSimplifiedView === 'simplified' ? document.querySelector('.layout') : document.body,
      )}
    </QuickBetslipProvider>
  );
}

const QuickBetMoneyInput = ({ desiredAmount, onChange }) => {
  const { loading } = useBetslipState();

  const [active, setActive] = React.useState(false);
  const currency = AccountManager.getAccountCurrency();

  const onKeyUp = useCallback(({ currentTarget, which }) => {
    // 13 is enter
    if (which === 13) {
      currentTarget.blur();
    }
  }, []);

  return (
    <MoneyInput
      active={active}
      currency={currency}
      dataTestId="betslip-amount"
      disabled={loading}
      label="Amount"
      onBlur={() => setActive(false)}
      onChange={onChange}
      onFocus={() => setActive(true)}
      onKeyUp={onKeyUp}
      touched
      value={desiredAmount}
    />
  );
};

QuickBetMoneyInput.propTypes = {
  disabled: PropTypes.bool,
  value: PropTypes.number,
};

const QuickBet = () => {
  const [desiredAmount, setDesiredAmount] = React.useState();

  return (
    <div className="quick-bet-info">
      <div className="quick-bet-input-container">
        <Label
          className="sub-title-text"
          message="When activating the Quick Bet mode you automatically agree to odds changes"
        />
        <QuickBetMoneyInput desiredAmount={desiredAmount} onChange={setDesiredAmount} />
      </div>
      <QuickBetAgreeButton desiredAmount={desiredAmount} />
    </div>
  );
};
